import { useState } from 'react';
import { Loader2, Play, CheckCircle2, XCircle } from 'lucide-react';
import { REF_NETS, TOL, runRefTest, runGissRefTest } from '../lib/aquaRef';

const f = (v, d = 2) => (v == null || !Number.isFinite(v) ? '–' : v.toFixed(d).replace('.', ','));
const Ok = ({ ok, id }) => (ok ? <CheckCircle2 size={14} color="#15803d" data-testid={id} /> : <XCircle size={14} color="#b91c1c" data-testid={id} />);

function NetResult({ t, r }) {
  if (r.error) return <p className="aq-err" data-testid={`aq-ref-error-${r.name}`}>{t.aqErr_calc} ({r.error})</p>;
  const c = r.counts;
  return (
    <div className="aq-res" data-testid={`aq-ref-result-${r.name}`}>
      <p className={r.ok ? 'aq-ok' : 'aq-err'} data-testid={`aq-ref-verdict-${r.name}`}><Ok ok={r.ok} id={`aq-ref-ok-${r.name}`} /> {r.name}: {r.ok ? t.aqRefPass : t.aqRefFail}</p>
      <small className="muted">{t.aqRefNet.replace('{j}', c.j).replace('{t}', c.t).replace('{r}', c.r).replace('{p}', c.pipes).replace('{u}', c.pumps).replace('{v}', c.valves)} · {r.hours} h · {r.ms} ms</small>
      <div className="aq-table-wrap"><table className="aq-table">
        <thead><tr><th /><th>{t.aqRefWhat}</th><th>{t.aqRefRef}</th><th>GISS</th><th>{t.aqRefDev}</th><th>{t.aqTol}</th></tr></thead>
        <tbody>
          <tr data-testid={`aq-ref-p-${r.name}`}><td><Ok ok={r.p.ok} /></td><td>{t.aqCol_p} ({r.p.n})</td><td>{f(r.p.ref[0])}–{f(r.p.ref[1])} bar</td><td>{f(r.p.got[0])}–{f(r.p.got[1])} bar</td><td>{f(r.p.d, 4)} bar</td><td>{f(TOL.p)} bar</td></tr>
          {r.tanks.map((x) => <tr key={x.id} data-testid={`aq-ref-tank-${r.name}-${x.id}`}><td><Ok ok={x.ok} /></td><td>{t.aqRefTank} {x.id}</td><td>{f(x.ref[0])}–{f(x.ref[1])} m</td><td>{f(x.got[0])}–{f(x.got[1])} m</td><td>{f(x.d, 4)} m</td><td>{f(TOL.lvl)} m</td></tr>)}
          {r.pumps.map((x) => <tr key={x.id} data-testid={`aq-ref-pump-${r.name}-${x.id}`}><td><Ok ok={x.ok} /></td><td>{t.aqRefPump} {x.id} ({t.aqRefOn})</td><td>Ø {f(x.ref)} l/s · {x.onRef} h</td><td>Ø {f(x.got)} l/s · {x.onGot} h</td><td>{f(x.d, 3)} l/s{x.st ? '' : ` · ${t.aqRefSt}`}</td><td>{f(x.tol)} l/s</td></tr>)}
        </tbody>
      </table></div>
      <small className="muted" data-testid={`aq-ref-engines-${r.name}`}>GISS: OWA-EPANET {r.ver} (WASM) · {t.aqRefRef}: {r.refEngine}</small>
    </div>
  );
}

function GissResult({ t, list }) {
  return (
    <div className="aq-res" data-testid="aq-ref-giss">
      <b>{t.aqRefGiss}</b>
      <small className="muted">{t.aqRefGissHint}</small>
      {list.map((r) => r.error ? <p key={r.name} className="aq-err" data-testid={`aq-ref-giss-error-${r.name}`}>{r.name}: {t.aqErr_calc} ({r.error})</p> : (
        <div key={r.name} data-testid={`aq-ref-giss-${r.name}`}>
          <p className={r.ok ? 'aq-ok' : 'aq-err'}><Ok ok={r.ok} id={`aq-ref-giss-ok-${r.name}`} /> {r.name}: {t.aqCol_p} {r.n}/{r.of} · {t.aqRefDev} {f(r.d, 4)} bar ({t.aqTol} {f(TOL.p)} bar)</p>
          {r.pumps.map((u) => <small key={u.id} className="muted" data-testid={`aq-ref-giss-pump-${r.name}-${u.id}`}>⚙️ {t.aqRefPump} {u.id}: {t.aqRefRef} {f(u.ref)} l/s · GISS {f(u.got)} l/s {u.ok ? '✔' : '✖'}</small>)}
          {r.miss.length > 0 && <small className="muted">{t.aqImpMiss}: {r.miss.join('; ')}</small>}
        </div>))}
    </div>
  );
}

// reference test tab: runs the bundled official EPANET examples unchanged and compares with stored native results
export function AquaRef({ t }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState([]);
  const [gis, setGis] = useState([]);
  const run = async () => {
    setBusy(true); setRes([]);
    const out = [];
    for (const n of REF_NETS) { out.push(await runRefTest(n).catch((e) => ({ name: n, error: String(e?.message || e) }))); setRes([...out]); }
    const g = [];
    setGis([]);
    for (const n of REF_NETS) { g.push(await runGissRefTest(n, t).catch((e) => ({ name: n, error: String(e?.message || e) }))); setGis([...g]); }
    setBusy(false);
  };
  return (
    <>
      <p className="muted">{t.aqRefHint}</p>
      <button className="btn btn-accent aq-run" data-testid="aq-ref-run-btn" disabled={busy} onClick={run}>{busy ? <Loader2 size={16} className="spin" /> : <Play size={16} />} {t.aqRefRun}</button>
      {res.map((r) => <NetResult key={r.name} t={t} r={r} />)}
      {gis.length > 0 && <GissResult t={t} list={gis} />}
      {res.length > 0 && !busy && <p className="aq-disc" data-testid="aq-ref-disc">{t.aqRefDisc}</p>}
    </>
  );
}
